import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "./Section";
import { ChevronRight } from "lucide-react";

/**
 * TrustSection — "Pourquoi OPTINA"
 *
 * Photos: Paris (Arc de Triomphe / Tour Eiffel) + Cantal (Viaduc de Garabit).
 * Orange "Mairie d'Aurillac" photo removed entirely from the homepage.
 *
 * Both photo badges use the same uniform style:
 *   bg #FFFFFF (opaque) · text #111111 · uppercase · border-radius 20px · padding 6px 14px
 *
 * Stats block (30+/24h/0€) now has thin vertical separators (#E0E0E0) between blocks.
 */

const PARIS = "/images/assurance-habitation-pas-cher-paris.webp";
const CANTAL = "/images/courtier-energie-aurillac-cantal-mobile.webp"; // Viaduc de Garabit

const STATS = [
  { n: "30+", l: "Partenaires" },
  { n: "24h", l: "Réponse" },
  { n: "100%", l: "Indépendant" },
];

export default function TrustSection() {
  const { scrollY } = useScroll();
  const yA = useTransform(scrollY, [500, 2500], [0, -80]);
  const yB = useTransform(scrollY, [500, 2500], [-40, 40]);

  return (
    <section data-testid="trust-section" className="relative py-24 md:py-32 bg-brand-sand overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 md:gap-14 items-center">
        {/* --- Two photos: Paris + Cantal — alignées, format horizontal homogène,
              nom de ville en overlay centré sur léger voile semi-transparent. --- */}
        <Reveal>
          <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {/* PARIS */}
            <div className="group">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.12)]">
                <motion.img
                  style={{ y: yA }}
                  src={PARIS}
                  alt="courtier-assurance-paris-arc-de-triomphe-tour-eiffel"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-[115%] object-cover"
                />
              </div>
              <div
                data-testid="trust-badge-paris"
                className="mt-5 flex justify-center"
              >
                <span className="inline-block text-[#1A1A1A] uppercase tracking-[0.32em] text-[11px] font-semibold leading-none">
                  Paris
                </span>
              </div>
            </div>

            {/* CANTAL — strictement même format / style que Paris */}
            <div className="group">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.12)]">
                <motion.img
                  style={{ y: yB }}
                  src={CANTAL}
                  alt="courtier-assurance-cantal-paysage"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-[115%] object-cover"
                />
              </div>
              <div
                data-testid="trust-badge-cantal"
                className="mt-5 flex justify-center"
              >
                <span className="inline-block text-[#1A1A1A] uppercase tracking-[0.32em] text-[11px] font-semibold leading-none">
                  Cantal
                </span>
              </div>
            </div>
          </div>
        </Reveal>

        {/* --- Right column: slogan + stats with vertical separators + CTA --- */}
        <div>
          <Reveal>
            <div className="overline">Pourquoi OPTINA</div>
          </Reveal>
          <Reveal delay={1}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4 tracking-tight leading-[1.02]">
              Je travaille pour vous.
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-4 font-editorial italic text-2xl md:text-3xl text-brand-ink/85 leading-snug">
              Pas pour une compagnie.
            </p>
          </Reveal>
          <Reveal delay={3}>
            <p className="mt-7 text-brand-ink/70 text-lg font-light leading-relaxed">
              Chez OPTINA, pas de centre d'appel. Un seul interlocuteur qui connaît votre dossier et
              négocie directement pour vous — avec un large panel de partenaires, sans conflit d'intérêt.
              Votre intérêt. Toujours.
            </p>
          </Reveal>

          {/* Stats with thin vertical separators (#E0E0E0) between blocks */}
          <Reveal delay={4}>
            <div
              data-testid="trust-stats"
              className="mt-10 flex items-center justify-between md:justify-start md:gap-0"
            >
              {STATS.map((s, i) => (
                <React.Fragment key={s.l}>
                  <div
                    data-testid={`trust-stat-${i}`}
                    className="flex-1 md:flex-initial md:px-10 text-center md:text-left first:md:pl-0 last:md:pr-0"
                  >
                    <div className="font-serif text-3xl md:text-4xl text-brand-ink">{s.n}</div>
                    <div className="mt-2 overline">{s.l}</div>
                  </div>
                  {i < STATS.length - 1 && (
                    <span
                      data-testid={`trust-stat-separator-${i}`}
                      aria-hidden="true"
                      className="block self-stretch w-px bg-[#E0E0E0]"
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </Reveal>

          <Reveal delay={5}>
            <Link
              to="/rendez-vous"
              data-testid="trust-cta"
              className="btn-press mt-10 inline-flex items-center gap-2 rounded-full bg-brand-ink hover:bg-black text-white px-7 py-4 text-sm font-semibold tracking-[0.2em] uppercase min-h-[48px]"
            >
              Prendre rendez-vous <ChevronRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
